import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";
import { sanitizeError } from "@/lib/errorUtils";

const settingsSchema = z.object({
  name: z.string().min(2).max(100),
  currency: z.string(),
  timezone: z.string(),
  week_send_dow: z.number().min(0).max(6),
  week_send_time: z.string(),
});

const CompanySettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState({ name: "", currency: "INR", timezone: "Asia/Kolkata", week_send_dow: 0, week_send_time: "09:00" });

  useEffect(() => { loadCompany(); }, [id]);

  const loadCompany = async () => {
    try {
      const { data, error } = await supabase.from("companies").select("*").eq("id", id).single();
      if (error) throw error;
      setCompanyName(data.name);
      setForm({ name: data.name, currency: data.currency, timezone: data.timezone, week_send_dow: data.week_send_dow, week_send_time: data.week_send_time_local });
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load settings"));
      navigate("/app");
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const validated = settingsSchema.parse(form);
      const { error } = await supabase.from("companies").update({
        name: validated.name, currency: validated.currency, timezone: validated.timezone,
        week_send_dow: validated.week_send_dow, week_send_time_local: validated.week_send_time,
      }).eq("id", id);
      if (error) throw error;
      toast.success("Settings saved");
      navigate(`/app/companies/${id}`);
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to save settings");
    } finally { setSaving(false); }
  };

  const days = [
    { value: 0, label: "Monday" }, { value: 1, label: "Tuesday" }, { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" }, { value: 4, label: "Friday" }, { value: 5, label: "Saturday" }, { value: 6, label: "Sunday" },
  ];

  if (loading) {
    return <AppShell title="Loading..." showBack backTo={`/app/companies/${id}`}><div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell title="Settings" subtitle={companyName} showBack backTo={`/app/companies/${id}`} companyId={id} companyName={companyName}>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Company settings</CardTitle>
              <CardDescription>Configure your company and reporting schedule</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2"><Label>Company name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="INR">INR (₹)</SelectItem><SelectItem value="USD">USD ($)</SelectItem><SelectItem value="EUR">EUR (€)</SelectItem><SelectItem value="GBP">GBP (£)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Timezone</Label>
                  <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Kolkata">IST</SelectItem><SelectItem value="America/New_York">EST</SelectItem><SelectItem value="America/Los_Angeles">PST</SelectItem>
                      <SelectItem value="Europe/London">GMT</SelectItem><SelectItem value="Europe/Paris">CET</SelectItem><SelectItem value="Asia/Singapore">SGT</SelectItem><SelectItem value="Asia/Dubai">GST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Weekly send day</Label>
                    <Select value={form.week_send_dow.toString()} onValueChange={(v) => setForm({ ...form, week_send_dow: parseInt(v) })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{days.map((d) => <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Send time</Label><Input type="time" value={form.week_send_time} onChange={(e) => setForm({ ...form, week_send_time: e.target.value })} required className="rounded-xl" /></div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => navigate(`/app/companies/${id}`)} className="flex-1 order-2 sm:order-1 rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={saving} className="flex-1 order-1 sm:order-2 rounded-xl"><Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save settings"}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default CompanySettings;
